import { dateTimeInZone } from '@/lib/dates';
import type { BoardRow } from './row';

type T = (key: string, values?: Record<string, unknown>) => string;

/** An ISO UTC instant (`2026-10-18T22:46:24.000Z`, or with an explicit offset); anything else is not one. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** Epoch ms of the service's `entered_at` (loadboard-carrier-quota D4), or null when it is missing or not an instant. */
export function enteredAtMs(at: string | null | undefined): number | null {
  if (typeof at !== 'string' || !ISO_INSTANT.test(at)) return null;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? ms : null;
}

/** How long ago, compact for a narrow column: `41 min`, `3 h` (under 48 h), `2 d`; never negative. Pure. */
export function compactAge(ms: number, t: T): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  if (minutes < 60) return t('loadBoard.leads.age.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return t('loadBoard.leads.age.hours', { count: hours });
  return t('loadBoard.leads.age.days', { count: Math.floor(hours / 24) });
}

/** Intl puts a narrow no-break space before AM / PM: plain spaces read (and test) the same. */
const plain = (s: string) => s.replace(/[  ]/g, ' ');

/**
 * A board row's Age (loadboard-carrier-quota): from Truckstop's posting instant when the row carries it, relative to
 * `now` (the screen's minute clock) with the exact posting time in the agency's zone as the title; else Truckstop's own
 * `age` (whole hours, "9+") as before. Pure.
 */
export function boardAge(row: Pick<BoardRow, 'entered_at' | 'age'>, now: number, zone: string, locale: string, t: T): { text: string; title: string | undefined } {
  const at = enteredAtMs(row.entered_at);
  if (at === null) return { text: row.age ?? '—', title: row.age ?? undefined };
  return { text: compactAge(now - at, t), title: plain(dateTimeInZone(row.entered_at, zone, locale)) };
}
